import type {
  LudoHistoryRow,
  LudoRollOutcome,
  LudoRollResult,
} from '@root/store/types';

// One row per roll, but history lists runs. These are a folded run's states.
export type RunResult =
  | 'IN_PROGRESS'
  | 'PAUSED'
  | 'WON'
  | 'ELIMINATED'
  | 'ABANDONED'
  | 'EXPIRED';

export interface RunSummary {
  journeyId: string;
  // The round that opened the run. Every roll after it is its own round.
  roundLabel: string;
  startedAt: string;
  rollAmount: number;
  paidRolls: number;
  totalSpent: number;
  // Highest multiplier tile the run landed on, if any.
  topMultiplier: number | null;
  result: RunResult;
  resumeDeadline: string | null;
  totalWin: number;
  net: number;
  rolls: HistoryRoll[];
}

export type HistoryRoll = LudoHistoryRow;

const byOldestFirst = (a: HistoryRoll, b: HistoryRoll): number =>
  new Date(a.roundTime).getTime() - new Date(b.roundTime).getTime();

// The furthest roll. Its figures are cumulative, so they cover the whole run.
function furthestAlong(a: HistoryRoll, b: HistoryRoll): HistoryRoll {
  const left = a.roundOutcome?.position ?? 0;
  const right = b.roundOutcome?.position ?? 0;
  if (left !== right) return right > left ? b : a;
  // An overshoot doesn't move, so the roll's position is where it started.
  return new Date(b.roundTime) >= new Date(a.roundTime) ? b : a;
}

function resultOf(row: HistoryRoll): RunResult {
  switch (row.journeyStatus) {
    case 'WON':
      return 'WON';
    case 'ELIMINATED':
      return 'ELIMINATED';
    case 'ABANDONED':
      return 'ABANDONED';
    case 'EXPIRED':
      return 'EXPIRED';
    default:
      return row.paused ? 'PAUSED' : 'IN_PROGRESS';
  }
}

// Which multiplier tile a roll finished on, if it finished on one at all.
function multiplierFor(row: HistoryRoll): number | null {
  const state = row.roundOutcome;
  const roll = state?.currentRoll;
  if (!roll || roll.payout <= 0) return null;
  const tile = state.board?.find((t) => t.position === roll.position);
  return tile?.multiplier ?? null;
}

/** Folds roll rows into one summary per run, grouping across pages. */
export function summariseRuns(rows: HistoryRoll[]): RunSummary[] {
  const runs = new Map<string, HistoryRoll[]>();

  for (const row of rows) {
    const journeyId = row.journeyId;
    if (!journeyId) continue;
    const group = runs.get(journeyId);
    if (group) group.push(row);
    else runs.set(journeyId, [row]);
  }

  const summaries = [...runs.entries()].map(([journeyId, group]) => {
    const rolls = [...group].sort(byOldestFirst);
    const first = rolls[0];
    const header = rolls.reduce(furthestAlong);
    const state = header.roundOutcome;

    const rollAmount = state.rollAmount ?? 0;
    const totalSpent = state.totalWagered ?? 0;
    const totalWin = state.totalPaid ?? 0;
    const multipliers = rolls
      .map(multiplierFor)
      .filter((m): m is number => m !== null);

    return {
      journeyId,
      // Stamped on every roll. Older runs fall back to their first roll.
      roundLabel: header.runReference ?? first.externalRoundId,
      startedAt: first.roundTime,
      rollAmount,
      // The stake is locked for the run, so the spend divides by it exactly.
      paidRolls: rollAmount > 0 ? Math.round(totalSpent / rollAmount) : 0,
      totalSpent,
      topMultiplier: multipliers.length ? Math.max(...multipliers) : null,
      result: resultOf(header),
      resumeDeadline: header.resumeDeadline ?? null,
      totalWin,
      net: totalWin - totalSpent,
      rolls,
    };
  });

  // Newest run first, the way the list reads.
  return summaries.sort(
    (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()
  );
}

// The list splits on this: paused runs sit under In progress.
export function isRunComplete(result: RunResult): boolean {
  return result !== 'IN_PROGRESS' && result !== 'PAUSED';
}

export interface RollLine {
  // 1-based, the way the detail screen numbers them.
  number: number;
  roundId: string;
  dice: number;
  from: number;
  to: number;
  charge: number;
  reward: number;
  outcome: LudoRollOutcome;
  multiplier: number | null;
  auto: boolean;
  // Where the pawn would have landed. Only differs on an overshoot.
  attempted: number;
  // A free credit is one roll or none, so this reads as 0 or 1.
  freeBefore: boolean;
  freeAfter: boolean;
  deadline: string | null;
  acceptedAt: string;
}

// An overshoot doesn't move, so the roll's position is where it started.
function startedAt(roll: LudoRollResult): number {
  return roll.outcome === 'OVERSHOOT'
    ? roll.position
    : roll.position - roll.dice;
}

/** One line per roll, oldest first. Charge is the step in the total, not the stake. */
export function rollLines(run: RunSummary): RollLine[] {
  let spentSoFar = 0;
  let freeBefore = false;

  return run.rolls.flatMap((row, index) => {
    const state = row.roundOutcome;
    const roll = state?.currentRoll;
    if (!roll) return [];

    const spent = state.totalWagered ?? 0;
    const charge = Math.max(0, spent - spentSoFar);
    spentSoFar = spent;

    const freeAfter = state.nextRollFree ?? false;
    const wasFree = freeBefore;
    freeBefore = freeAfter;

    return [
      {
        number: index + 1,
        roundId: row.externalRoundId,
        dice: roll.dice,
        from: startedAt(roll),
        to: roll.position,
        charge,
        reward: roll.payout,
        outcome: roll.outcome,
        multiplier:
          state.board?.find((tile) => tile.position === roll.position)
            ?.multiplier ?? null,
        auto: row.bets?.[0]?.autoRoll ?? false,
        attempted: startedAt(roll) + roll.dice,
        freeBefore: wasFree,
        freeAfter,
        deadline: row.bets?.[0]?.rollDeadline ?? state.rollDeadline ?? null,
        acceptedAt: row.roundTime,
      },
    ];
  });
}

// Free rolls don't show up in the money, so count them off the charges.
export function rollCounts(lines: RollLine[]): { paid: number; free: number } {
  const paid = lines.filter((line) => line.charge > 0).length;
  return { paid, free: lines.length - paid };
}

export interface OutcomeTotals {
  spent: number;
  multipliers: number;
  finish: number;
  won: number;
  net: number;
}

/** The five figures every end-of-run card shows. Only a finished run has a finish reward. */
export function outcomeTotals(
  state:
    | {
        totalWagered?: number;
        totalPaid?: number;
        currentRoll?: { outcome: LudoRollOutcome; payout: number } | null;
      }
    | undefined,
  finished: boolean
): OutcomeTotals {
  const spent = state?.totalWagered ?? 0;
  const won = state?.totalPaid ?? 0;
  const roll = state?.currentRoll;
  const finish = finished && roll?.outcome === 'FINISH' ? roll.payout : 0;
  return {
    spent,
    multipliers: Math.max(0, won - finish),
    finish,
    won,
    net: won - spent,
  };
}
